import React from "react";
import { useEffect } from "react";
import { CtaHeader } from "./CtaHeader";
import { Offers, Items } from "./Offers";
import { ChangeServices } from "./ChangeServices";
import { DashboardBoxSmall } from "./DashboardBoxSmall";
import Styles from "./Dashboard.module.scss";
import { useTranslation, Trans } from "react-i18next";
import classNames from "classnames";
import useUser from "@/shared/hooks/useUser";
import { MyShortOffersBaseRoute, gasOffer, electricityOffer } from "@/routes/offer";
import { DashboardPrizesRoute, DashboardQuestionsAndAnswersRoute } from "@/routes/dashboard";
import { baseRoute as topDealsRoute } from "@/routes/topdeals";
import { Icon } from "@/App/components/@remote";
import { useTheme } from "../../../../shared/styles/useTheme";
import { getChangeServices } from "../../../../api/changeservice";
import { getShortReminders } from "../../../../api/reminder";
import { useLayout } from "@/cms/components/LayoutContextAPI/useLayout";
import { useQuery } from "react-query";
import ReferralCard from "./ReferralCard";
import { getUserId } from "@/api/localStorage";
import { checkReferralEligibility } from "@/api/referral";
export function isMissingData(cs: Remote.ChangeService) {
  return cs.hasMissingData;
}
export const Dashboard = () => {
  const { t } = useTranslation();
  const { data: user, isLoading } = useUser();
  const { dispatch } = useLayout();
  const userId = getUserId();
  useEffect(() => {
    dispatch({
      type: "SET_LAYOUT_STATE",
      payload: {
        header: { show: false, simple: false },
        sidenav: true,
        footer: true,
        breadcrumbs: true,
        pageType: "wide",
        bgColor: "#f7f7f7",
      },
    });
  }, [dispatch]);

  const { data: shortReminders = [] } = useQuery(
    ["shortReminders", user?.id],
    () => getShortReminders(user?.id ?? ""),
    { enabled: !!user?.id }
  );

  const { data: changeServices = [] } = useQuery<Remote.ChangeService[] | null>(
    ["changeServices", user?.id],
    () => getChangeServices(user?.id ?? ""),
    { enabled: !!user?.id }
  );

  const hasMissingData = changeServices?.some(isMissingData);
  const hasElectricityChangeServices = changeServices?.filter((ch) => ch.category === "ELECTRICITY").length !== 0;
  const hasGasChangeServices = changeServices?.filter((ch) => ch.category === "GAS").length !== 0;
  const hideHeader = !hasMissingData && hasElectricityChangeServices && hasGasChangeServices;

  const { data: eligibilityData } = useQuery(["referral-eligibility", userId], () => checkReferralEligibility(userId), {
    enabled: !!userId,
  });
  const offersLinks = [MyShortOffersBaseRoute, gasOffer, electricityOffer];
  const { primaryColor } = useTheme();

  const itemsOffers = [
    {
      icon: [
        <p key="offers_1" className={Styles.remindersItem}>
          {shortReminders?.length}
        </p>,
      ],
      name: <b>{t("dashboard.requestedOffers")}</b>,
    },
  ];

  const itemsPrizes = [
    {
      icon: [
        <p key="itemsPrizes_1" className={Styles.prizesItem}>
          0,00 €
        </p>,
      ],
      name: t("dashboard.inExamination"),
    },
    {
      icon: [
        <p key="itemsPrizes_2" className={Styles.prizesItem}>
          0,00 €
        </p>,
      ],
      name: t("dashboard.requestNow"),
    },
    {
      icon: [
        <p key="itemsPrizes_3" className={Styles.prizesItem}>
          0,00 €
        </p>,
      ],
      name: t("dashboard.alreadyPaid"),
    },
  ];

  const itemsTopDeals = [
    {
      icon: [
        <Icon key="itemsTopDeals_1" className={classNames(Styles.topDealsItem, primaryColor)} name={"checkThick"} />,
      ],
      name: t("dashboard.exclusiveOffers"),
    },
    {
      icon: [
        <Icon key="itemsTopDeals_2" className={classNames(Styles.topDealsItem, primaryColor)} name={"checkThick"} />,
      ],
      name: t("dashboard.couponsAndPrizes"),
    },
    {
      icon: [
        <Icon key="itemsTopDeals_3" className={classNames(Styles.topDealsItem, primaryColor)} name={"checkThick"} />,
      ],
      name: t("dashboard.technicalFinancesAndEnergy"),
    },
  ];

  return (
    <div className={Styles.dashboardWrapper}>
      <div className={Styles.welcome}>
        {!isLoading ? (
          <Trans
            i18nKey={"dashboard.title"}
            values={{
              name: `${user?.firstname || ""} ${user?.lastname || ""}`,
              comma: user?.firstname || user?.lastname ? ", " : "",
            }}
          >
            <h1 className={classNames(Styles.title, "oswald")} />
          </Trans>
        ) : (
          <h1 style={{ height: "40px" }} className={classNames(Styles.title, "oswald")} />
        )}
        <p className={classNames(Styles.subTitle, "oswald")}>{t("dashboard.subTitle")}</p>
      </div>
      {!eligibilityData?.showRef && (
        <CtaHeader changeServices={changeServices} className={classNames(Styles.ctaHeaderStyles)} />
      )}
      <div className={Styles.dashboardBoxes}>
        {eligibilityData?.showRef && (
          <>
            <CtaHeader
              changeServices={changeServices}
              className={classNames(Styles.ctaHeaderStyles, Styles.dashboardBoxStyles, Styles.ctaHeaderCardStyles)}
            />
            <ReferralCard className={classNames(!hideHeader && Styles.dashboardBoxStyles)} />
          </>
        )}
        {shortReminders !== null ? (
          <Offers
            shortReminders={shortReminders}
            links={offersLinks}
            className={Styles.dashboardBoxStyles}
            footertext={t("dashboard.getNewGasOffer")}
            items={itemsOffers}
          />
        ) : (
          <span></span>
        )}
        {changeServices !== null ? (
          <ChangeServices className={Styles.dashboardBoxStyles} changeServices={changeServices} />
        ) : (
          <span></span>
        )}
      </div>

      <div className={Styles.smallBoxes}>
        <DashboardBoxSmall
          className={Styles.dashboardBoxSmallStyles}
          title={t("dashboard.myPrizes")}
          footertext={t("dashboard.LearnMore")}
          link={DashboardPrizesRoute}
        >
          <Items className={Styles.itemPrizes} items={itemsPrizes} />
        </DashboardBoxSmall>
        <DashboardBoxSmall
          className={Styles.dashboardBoxSmallStylesOffers}
          title={t("dashboard.topDeals")}
          footertext={t("dashboard.LearnMore")}
          link={topDealsRoute}
        >
          <Items items={itemsTopDeals} />
        </DashboardBoxSmall>
        <DashboardBoxSmall
          className={Styles.dashboardBoxSmallStyles}
          title={t("dashboard.questionsAndAnswers")}
          footertext={t("dashboard.LearnMore")}
          link={DashboardQuestionsAndAnswersRoute}
        >
          {t("dashboard.haveMoreQuestions")}
        </DashboardBoxSmall>
      </div>
    </div>
  );
};
